import { Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { TranslatePipe } from '@cmi/addin-core';
import { Button, Notification } from '@cmi/ui';
import { FilingsService } from '../filings.service';
import { CurrentMailSource } from './current-mail-source';
import { FilingPipelineService, FilingRequest, FolderTarget } from './filing-pipeline.service';
import { FiledTarget, MAX_FILINGS } from './mail-upload.models';
import { MailService } from './mail.service';

type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

@Component({
  selector: 'cmi-mail-upload',
  imports: [TranslatePipe, Button, Notification],
  templateUrl: './mail-upload.html'
})
export class MailUpload {
  private readonly _mailService = inject(MailService);
  private readonly _filingsService = inject(FilingsService);
  private readonly _filingPipeline = inject(FilingPipelineService);

  /** Null until a dossier is picked. The button shows either way, disabled. */
  public readonly dossierGuid = input<string | null>(null);
  public readonly folder = input<FolderTarget>(null);
  /** The document fields as they go into the request, or made from the mail once it is read. */
  public readonly felder = input.required<FilingRequest['felder']>();
  /** False while the mask is unknown or a mandatory field is empty. */
  public readonly fieldsComplete = input<boolean>(false);
  public readonly uploaded = output<FiledTarget>();

  /** Resets itself whenever another target is picked, so a result never outlives it. */
  protected readonly status = linkedSignal<string, UploadStatus>({
    source: () => {
      const folder = this.folder();
      return `${this.dossierGuid() ?? ''}/${typeof folder === 'function' ? 'new' : (folder ?? '')}`;
    },
    computation: () => 'idle'
  });

  /** What the request needs before it can be sent at all. */
  protected readonly ready = computed(() => this.dossierGuid() !== null && this.fieldsComplete());

  /**
   * Unlike `status` this survives a target change, so picking another folder
   * mid-upload cannot un-disable the button and let the mail be filed twice.
   */
  private readonly _uploading = signal<boolean>(false);

  protected readonly uploading = this._uploading.asReadonly();

  protected readonly errorKey = signal<string>('mailUpload.errorUpload');
  protected readonly errorDetail = signal<string | null>(null);

  /** Set for an error no retry can fix, which gets this hint and no retry. */
  protected readonly errorHint = signal<string | null>(null);

  protected readonly maxFilings = { max: String(MAX_FILINGS) };

  protected async onUpload(): Promise<void> {
    const dossierId = this.dossierGuid();

    if (this._uploading() || !this.ready() || dossierId === null) {
      return;
    }

    // Read once up front: the user can pick another target while this runs, and
    // the mail must be filed into — and reported as — the one that was clicked.
    // The source does the same for the mail itself, which a pinned pane lets the
    // user change just as freely.
    const request = { dossierId, ordnerId: this.folder(), felder: this.felder() };
    const source = new CurrentMailSource(this._mailService, this._filingsService);

    this._uploading.set(true);
    this.status.set('uploading');
    this.errorDetail.set(null);

    // Confirmed as soon as the server has the mail. The record on the mail and
    // the CMI mark follow, and failing either does not fail a filed mail.
    const outcome = await this._filingPipeline.file(source, request, (filed): void => {
      this._uploading.set(false);
      // Terminal rather than back to idle: the parent moves on to the
      // confirmation, and until it does the button must not invite a second upload.
      this.status.set('done');
      this.uploaded.emit(filed);
    });

    if (outcome.status === 'failed-retryable' || outcome.status === 'failed-final') {
      this._uploading.set(false);
      this.errorKey.set(outcome.errorKey);
      this.errorHint.set(outcome.hintKey);
      this.errorDetail.set(outcome.detail);
      this.status.set('error');
    }
  }
}
